/**
 * The form for logging something, used in two places: the Add screen, where
 * it starts empty, and the Scan screen, where it starts filled in with
 * whatever the barcode or the label photo found.
 *
 * That second case is the important one. Numbers that came from a barcode
 * lookup or from reading a photo are a *suggestion*: Open Food Facts is
 * crowd-sourced and can be wrong, and a photo can be misread. So they always
 * land in an editable form for Sam to confirm, and are never saved on her
 * behalf.
 */
import { useRef, useState, type FormEvent } from "react";
import { api, type Entry } from "../lib/api";
import { today } from "../lib/day";
import {
  EXTRA_NUTRIENTS,
  NUTRIENTS,
  NUTRIENT_GROUPS,
  type ExtraKey,
} from "../../server/nutrients";

export type Draft = {
  kind: "food" | "drink" | "exercise";
  name: string;
  servings: string;
  calories: string;
  protein_g: string;
  carbs_g: string;
  fat_g: string;
  fiber_g: string;
  sugar_g: string;
  sodium_mg: string;
  minutes: string;
  barcode: string | null;
  source: "barcode" | "label" | "manual" | "saved";
} & Record<ExtraKey, string>;

export const emptyDraft: Draft = {
  kind: "food",
  name: "",
  servings: "1",
  calories: "",
  protein_g: "",
  carbs_g: "",
  fat_g: "",
  fiber_g: "",
  sugar_g: "",
  sodium_mg: "",
  minutes: "",
  barcode: null,
  source: "manual",
  ...(Object.fromEntries(EXTRA_NUTRIENTS.map((n) => [n.key, ""])) as Record<ExtraKey, string>),
};

/**
 * Turns what's typed in a box into a number the server will accept.
 *
 * Empty means "not known", which is different from zero: a label with no
 * fibre line should not claim the food has no fibre.
 */
function numberOrNull(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

export function EntryForm({
  draft,
  onChange,
  onSaved,
}: {
  draft: Draft;
  onChange: (draft: Draft) => void;
  onSaved: (entry: Entry) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  const set = (patch: Partial<Draft>) => onChange({ ...draft, ...patch });

  // Anything a scan filled in is always on show, because Sam has to be able to
  // see a number to confirm it. Remembered rather than worked out fresh, so a
  // box doesn't vanish the moment she clears it to retype.
  const filled = useRef(new Set<string>());
  for (const n of NUTRIENTS) if (draft[n.key] !== "") filled.current.add(n.key);
  const visible = NUTRIENTS.filter((n) => showDetail || filled.current.has(n.key));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (draft.name.trim() === "") {
      setError("What should this be called?");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const servings = numberOrNull(draft.servings) ?? 1;

      // The numbers on screen are for one serving, as they appear on a label.
      // What gets stored is what was actually eaten, so they are multiplied
      // through here -- once, in one place.
      const per = (text: string) => {
        const value = numberOrNull(text);
        return value === null ? null : Math.round(value * servings * 100) / 100;
      };

      const entry = await api.addEntry({
        day: today(),
        kind: draft.kind,
        name: draft.name.trim(),
        servings,
        calories: per(draft.calories),
        protein_g: draft.kind === "exercise" ? null : per(draft.protein_g),
        carbs_g: draft.kind === "exercise" ? null : per(draft.carbs_g),
        fat_g: draft.kind === "exercise" ? null : per(draft.fat_g),
        fiber_g: draft.kind === "exercise" ? null : per(draft.fiber_g),
        sugar_g: draft.kind === "exercise" ? null : per(draft.sugar_g),
        sodium_mg: draft.kind === "exercise" ? null : per(draft.sodium_mg),
        ...Object.fromEntries(
          EXTRA_NUTRIENTS.map((n) => [n.key, draft.kind === "exercise" ? null : per(draft[n.key])]),
        ),
        minutes: numberOrNull(draft.minutes),
        barcode: draft.barcode,
        source: draft.source,
      });
      onSaved(entry);
    } catch (problem) {
      setError((problem as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const isExercise = draft.kind === "exercise";

  return (
    <form onSubmit={submit}>
      <div className="chips" style={{ marginBottom: 14 }}>
        {(["food", "drink", "exercise"] as const).map((kind) => (
          <button
            type="button"
            key={kind}
            className="chip"
            aria-pressed={draft.kind === kind}
            onClick={() => set({ kind })}
          >
            {kind === "food" ? "🍽️ Food" : kind === "drink" ? "🥤 Drink" : "🏃 Exercise"}
          </button>
        ))}
      </div>

      <label className="field">
        <span>{isExercise ? "What did you do?" : "What was it?"}</span>
        <input
          className="input"
          value={draft.name}
          onChange={(event) => set({ name: event.target.value })}
          placeholder={isExercise ? "Walk with the dog" : "Greek yoghurt"}
          // Opens the keyboard straight away on an empty form, which saves a
          // tap every single time something is logged.
          autoFocus={draft.name === ""}
        />
      </label>

      {isExercise ? (
        <div className="field-row">
          <label className="field">
            <span>Minutes</span>
            <input
              className="input"
              inputMode="decimal"
              value={draft.minutes}
              onChange={(event) => set({ minutes: event.target.value })}
              placeholder="30"
            />
          </label>
          <label className="field">
            <span>Calories burned</span>
            <input
              className="input"
              inputMode="decimal"
              value={draft.calories}
              onChange={(event) => set({ calories: event.target.value })}
              placeholder="180"
            />
          </label>
        </div>
      ) : (
        <>
          <div className="field-row">
            <label className="field">
              <span>Servings</span>
              <input
                className="input"
                inputMode="decimal"
                value={draft.servings}
                onChange={(event) => set({ servings: event.target.value })}
              />
            </label>
            <label className="field">
              <span>Calories (per serving)</span>
              <input
                className="input"
                inputMode="decimal"
                value={draft.calories}
                onChange={(event) => set({ calories: event.target.value })}
                placeholder="150"
              />
            </label>
          </div>

          <div className="field-row">
            <label className="field">
              <span>Protein (g)</span>
              <input
                className="input"
                inputMode="decimal"
                value={draft.protein_g}
                onChange={(event) => set({ protein_g: event.target.value })}
              />
            </label>
            <label className="field">
              <span>Carbs (g)</span>
              <input
                className="input"
                inputMode="decimal"
                value={draft.carbs_g}
                onChange={(event) => set({ carbs_g: event.target.value })}
              />
            </label>
          </div>

          <label className="field">
            <span>Fat (g)</span>
            <input
              className="input"
              inputMode="decimal"
              value={draft.fat_g}
              onChange={(event) => set({ fat_g: event.target.value })}
            />
          </label>

          {/* The nutrients most people don't type in by hand are folded away,
              so the everyday case is a short form. */}
          {NUTRIENT_GROUPS.map((group) => {
            const fields = visible.filter((n) => n.group === group);
            if (fields.length === 0) return null;
            return (
              <div key={group}>
                <h3 className="nutrient-group">{group}</h3>
                <div className="nutrient-fields">
                  {fields.map((n) => (
                    <label className="field" key={n.key}>
                      <span>
                        {n.label} ({n.unit})
                      </span>
                      <input
                        className="input"
                        inputMode="decimal"
                        value={draft[n.key]}
                        onChange={(event) => set({ [n.key]: event.target.value })}
                      />
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          {!showDetail && (
            <button
              type="button"
              className="chip"
              style={{ marginBottom: 14 }}
              onClick={() => setShowDetail(true)}
            >
              + more nutrients
            </button>
          )}
        </>
      )}

      {error && (
        <div className="notice notice--bad" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      <button className="button button--full" disabled={saving}>
        {saving ? "Saving…" : "Log it"}
      </button>
    </form>
  );
}
